/**
 * Role Repository
 * Handles all role-related API calls
 */

import { apiClient } from '../client'

export interface Role {
    id: number
    tenant_id: number
    en_name: string
    ar_name?: string | null
    status: number
    visit_type_ids?: number[] | null
    special_visit_limit: number
    visit_types?: { id: number; en_name?: string; ar_name?: string }[] | null
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date
    CreatedByUser?: {
        id: number
        en_name: string
        ar_name?: string | null
    } | null
    UpdatedByUser?: {
        id: number
        en_name: string
        ar_name?: string | null
    } | null
    total_users?: number;
}

export interface CreateRoleRequest {
    tenant_id: number
    en_name: string
    ar_name?: string
    special_visit_limit: number;
    status?: number
    permission_ids?: string // Comma-separated permission IDs
    visit_type_ids?: number[]
    // Note: created_by is automatically set by backend from logged-in user
}

export interface UpdateRoleRequest {
    tenant_id?: number
    en_name?: string
    ar_name?: string
    special_visit_limit?: number
    status?: number
    permission_ids?: string // Comma-separated permission IDs
    visit_type_ids?: number[]
    // Note: updated_by is automatically set by backend from logged-in user
}

export interface PaginatedResponse<T> {
    data: T[]
    total: number
    page: number
    limit: number
    totalPages: number
}

class RoleRepository {
    /**
     * Get all roles with pagination
     */
    async getAll(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<Role>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        // Add filters if provided
        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
        }

        return apiClient.get<PaginatedResponse<Role>>(`/roles?${params.toString()}`)
    }

    /**
     * Get role by ID
     */
    async getById(id: number): Promise<Role> {
        return apiClient.get<Role>(`/roles/${id}`)
    }

    /**
     * Create a new role
     */
    async create(data: CreateRoleRequest): Promise<Role> {
        return apiClient.post<Role>('/roles', data)
    }

    /**
     * Update an existing role
     */
    async update(id: number, data: UpdateRoleRequest): Promise<Role> {
        return apiClient.put<Role>(`/roles/${id}`, data)
    }

    /**
     * Delete a role
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/roles/${id}`)
    }

    // Total Roles Count
    async totalRoleCount(): Promise<number> {
        const response = await apiClient.get('/roles/total-roles');
        return response.totalRoles;

    }

    // get total roles

    async totalRoles(): Promise<Role[]> {
        const response = await apiClient.get<Role[]>('/roles/all-roles');
        return response;

    }

    // Get analytics data (total count + role analytics for chart)
    async getAnalytics(): Promise<{ totalRoles: number; roleAnalytics: Array<{ roleName: string; userCount: string }> }> {
        return apiClient.get('/roles/analytics');
    }

    /**
     * Export roles to Excel
     */
    async exportToExcel(filters?: { search?: string; status?: string }): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.search) {
            params.append('search', filters.search)
        }
        if (filters?.status) {
            params.append('status', filters.status)
        }

        const baseUrl = `/roles/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' });
    }
}

// Export singleton instance
export const roleRepository = new RoleRepository()

